import type { PortraitStudent } from '@/api/types/portrait'
import type { PortraitsPlacedFilter } from '@/lib/portraits/types/portraits-placed-filter'

/**
 * Whether a student counts as placed: the student-level flag (`studentUsedOnPage`, true when ANY of
 * their portraits sits on a page), not the per-portrait `usedOnPage`. The Placed filter and the
 * card's "Placed" badge both ask this, so a card shows the badge exactly when "Yes" lists it. A
 * missing flag reads as not placed, as ClikArt's missing `placedFlag` does.
 */
export function isPortraitStudentPlaced(student: Pick<PortraitStudent, 'studentUsedOnPage'>) {
  return student.studentUsedOnPage === true
}

/**
 * The Portraits tray's Placed filter, applied client-side over the roster like the ClikArt,
 * Backgrounds and Photos trays do (`filterClikArtByPlaced`, `filterBackgroundsByPlaced`): `'any'`
 * keeps everyone, `'yes'` only placed students, `'no'` only students not placed. Order is kept.
 */
export function filterPortraitStudentsByPlaced<
  T extends Pick<PortraitStudent, 'studentUsedOnPage'>,
>(students: T[], filter: PortraitsPlacedFilter): T[] {
  if (filter === 'any') {
    return students
  }

  const wantPlaced = filter === 'yes'

  return students.filter((student) => isPortraitStudentPlaced(student) === wantPlaced)
}
